import React, { useState } from 'react';
import {
  LightBulbIcon,
  ClipboardDocumentListIcon,
  PencilSquareIcon,
  CogIcon,
  BeakerIcon,
  TruckIcon,
  CheckBadgeIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import { StageDocuments } from './StageDocuments';

// ─── Stage definitions (mirror Projects.jsx) ─────────────────────────────────
export const PROJECT_STAGES = [
  {
    key: 'initiation',
    label: 'Initiation',
    shortLabel: 'Init',
    icon: LightBulbIcon,
    color: 'text-gray-600',
    bg: 'bg-gray-100',
    activeBg: 'bg-gray-600',
    ring: 'ring-gray-400',
    dot: 'bg-gray-400',
    description: 'Project kicked off, scope defined, stakeholders identified.',
  },
  {
    key: 'planning',
    label: 'Planning',
    shortLabel: 'Plan',
    icon: ClipboardDocumentListIcon,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    activeBg: 'bg-blue-600',
    ring: 'ring-blue-400',
    dot: 'bg-blue-400',
    description: 'Timeline, resources and milestones are being finalised.',
  },
  {
    key: 'design',
    label: 'Design',
    shortLabel: 'Design',
    icon: PencilSquareIcon,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    activeBg: 'bg-purple-600',
    ring: 'ring-purple-400',
    dot: 'bg-purple-400',
    description: 'Blueprints, drawings and design specs are being prepared.',
  },
  {
    key: 'execution',
    label: 'Execution',
    shortLabel: 'Exec',
    icon: CogIcon,
    color: 'text-yellow-600',
    bg: 'bg-yellow-50',
    activeBg: 'bg-yellow-500',
    ring: 'ring-yellow-400',
    dot: 'bg-yellow-400',
    description: 'Active work in progress; materials procured, work underway.',
  },
  {
    key: 'testing',
    label: 'Testing',
    shortLabel: 'Test',
    icon: BeakerIcon,
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    activeBg: 'bg-orange-500',
    ring: 'ring-orange-400',
    dot: 'bg-orange-400',
    description: 'Quality checks, inspections and punch-list items in progress.',
  },
  {
    key: 'handover',
    label: 'Handover',
    shortLabel: 'Handover',
    icon: TruckIcon,
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
    activeBg: 'bg-indigo-600',
    ring: 'ring-indigo-400',
    dot: 'bg-indigo-400',
    description: 'Deliverables handed to client; documentation submitted.',
  },
  {
    key: 'completed',
    label: 'Completed',
    shortLabel: 'Done',
    icon: CheckBadgeIcon,
    color: 'text-green-600',
    bg: 'bg-green-50',
    activeBg: 'bg-green-600',
    ring: 'ring-green-400',
    dot: 'bg-green-500',
    description: 'Project closed. All obligations fulfilled.',
  },
];

// ─── Helper ───────────────────────────────────────────────────────────────────
function stageIndex(key) {
  return PROJECT_STAGES.findIndex(s => s.key === key);
}

// ─── Component ────────────────────────────────────────────────────────────────
/**
 * ProjectStageFlowchart
 * Props:
 *   projectId     {string}  MongoDB project _id — needed for stage documents
 *   stage         {string}  current stage key (e.g. 'execution')
 *   onStageChange {fn}      optional – called with new stage key when user clicks a node
 *                           (omit to render read-only)
 */
export function ProjectStageFlowchart({ projectId, stage = 'initiation', onStageChange }) {
  const currentIdx = stageIndex(stage);
  const [hovered, setHovered]               = useState(null);
  const [selectedStage, setSelectedStage]   = useState(null); // key of stage whose docs panel is open
  const hoveredStage  = hovered !== null ? PROJECT_STAGES[hovered] : null;
  const activeStage   = PROJECT_STAGES[currentIdx];
  const selectedDef   = selectedStage ? PROJECT_STAGES.find(s => s.key === selectedStage) : null;

  const handleNodeClick = (s) => {
    // Only toggle the docs panel — do NOT auto-update project stage
    setSelectedStage(prev => prev === s.key ? null : s.key);
  };

  const handleSetStage = async (stageKey) => {
    if (onStageChange) await onStageChange(stageKey);
  };

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="px-5 pt-4 pb-3 flex items-center justify-between border-b border-gray-50">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Project Stage</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {currentIdx + 1} of {PROJECT_STAGES.length} stages complete
          </p>
        </div>
        {/* Active stage pill */}
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${activeStage.bg} ${activeStage.color}`}>
          <activeStage.icon className="w-3.5 h-3.5" />
          {activeStage.label}
        </span>
      </div>

      {/* ── Progress bar ───────────────────────────────────────────── */}
      <div className="px-5 pt-3">
        <div className="relative h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
            style={{
              width: `${((currentIdx + 1) / PROJECT_STAGES.length) * 100}%`,
              background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
            }}
          />
        </div>
        <p className="text-right text-[10px] text-gray-400 mt-0.5">
          {Math.round(((currentIdx + 1) / PROJECT_STAGES.length) * 100)}% pipeline progress
        </p>
      </div>

      {/* ── Flowchart nodes ────────────────────────────────────────── */}
      <div className="px-5 pb-5 pt-2">
        {/* Desktop: horizontal flow */}
        <div className="hidden sm:flex items-start gap-0">
          {PROJECT_STAGES.map((s, i) => {
            const done    = i < currentIdx;
            const current = i === currentIdx;
            const future  = i > currentIdx;
            const isHovered = hovered === i;
            const Icon = s.icon;

            return (
              <React.Fragment key={s.key}>
                {/* Stage node */}
                <div
                  className="flex flex-col items-center gap-1.5 flex-1 min-w-0 cursor-pointer group"
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => handleNodeClick(s)}
                  title={`${s.label} — click to view/upload documents`}
                >
                  {/* Circle */}
                  <div
                    className={`
                      relative w-10 h-10 rounded-full flex items-center justify-center
                      transition-all duration-200 ring-2
                      ${done
                        ? 'bg-indigo-600 ring-indigo-200 shadow-md shadow-indigo-100'
                        : current
                          ? `${s.activeBg} ${s.ring} shadow-lg ring-offset-1`
                          : `bg-gray-50 ring-gray-200 ${isHovered ? 'ring-gray-300 scale-110' : ''}`
                      }
                      ${selectedStage === s.key ? 'ring-4 scale-110 shadow-lg' : ''}
                      group-hover:scale-110
                    `}
                  >
                    {done ? (
                      <CheckCircleSolid className="w-5 h-5 text-white" />
                    ) : (
                      <Icon className={`w-5 h-5 ${current ? 'text-white' : 'text-gray-400'}`} />
                    )}
                    {/* Pulse ring for current */}
                    {current && (
                      <span className={`absolute inset-0 rounded-full ${s.dot} opacity-20 animate-ping`} />
                    )}
                  </div>

                  {/* Label */}
                  <span className={`text-[10px] font-semibold text-center leading-tight truncate w-full text-center px-0.5
                    ${done ? 'text-indigo-600' : current ? s.color : 'text-gray-400'}
                  `}>
                    {s.shortLabel}
                  </span>
                </div>

                {/* Connector arrow */}
                {i < PROJECT_STAGES.length - 1 && (
                  <div className="flex items-center mt-5 flex-shrink-0 w-4">
                    <ChevronRightIcon
                      className={`w-3.5 h-3.5 transition-colors ${
                        i < currentIdx ? 'text-indigo-400' : 'text-gray-200'
                      }`}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Mobile: vertical flow */}
        <div className="flex sm:hidden flex-col gap-0">
          {PROJECT_STAGES.map((s, i) => {
            const done    = i < currentIdx;
            const current = i === currentIdx;
            const Icon = s.icon;
            const isLast = i === PROJECT_STAGES.length - 1;

            return (
              <div
                key={s.key}
                className="flex items-start gap-3 cursor-pointer"
                onClick={() => handleNodeClick(s)}
              >
                {/* Spine */}
                <div className="flex flex-col items-center">
                  <div
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center ring-2 z-10 relative
                      ${done
                        ? 'bg-indigo-600 ring-indigo-200'
                        : current
                          ? `${s.activeBg} ${s.ring}`
                          : 'bg-gray-50 ring-gray-200'
                      }
                    `}
                  >
                    {done
                      ? <CheckCircleSolid className="w-4 h-4 text-white" />
                      : <Icon className={`w-4 h-4 ${current ? 'text-white' : 'text-gray-400'}`} />
                    }
                  </div>
                  {!isLast && (
                    <div className={`w-0.5 flex-1 min-h-[20px] mt-0.5 ${i < currentIdx ? 'bg-indigo-300' : 'bg-gray-100'}`} />
                  )}
                </div>

                {/* Text */}
                <div className="pb-3 flex-1">
                  <span className={`text-xs font-semibold ${done ? 'text-indigo-600' : current ? s.color : 'text-gray-400'}`}>
                    {s.label}
                    {current && <span className="ml-2 text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">Current</span>}
                  </span>
                  {current && (
                    <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">{s.description}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Hover tooltip / description ─────────────────────────── */}
        <div
          className={`mt-3 transition-all duration-200 overflow-hidden ${
            hoveredStage ? 'max-h-16 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          {hoveredStage && (
            <div className={`rounded-xl px-4 py-2.5 ${hoveredStage.bg} flex items-start gap-2`}>
              <hoveredStage.icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${hoveredStage.color}`} />
              <div>
                <p className={`text-xs font-semibold ${hoveredStage.color}`}>{hoveredStage.label}</p>
                <p className="text-[11px] text-gray-500 leading-snug">{hoveredStage.description}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Stage Documents Panel ────────────────────────────────── */}
      {selectedDef && projectId && (
        <div className={`border-t ${selectedDef.bg} px-5 pb-5 pt-4`}>
          {/* Panel header */}
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <selectedDef.icon className={`w-4 h-4 ${selectedDef.color}`} />
              <span className={`text-sm font-semibold ${selectedDef.color}`}>
                {selectedDef.label} — Documents
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* Only show "Set as Current" if this stage isn't already active */}
              {selectedDef.key !== stage && onStageChange && (
                <button
                  onClick={() => handleSetStage(selectedDef.key)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors
                    ${selectedDef.color} border-current hover:bg-black/5`}
                >
                  ✓ Set as Current Stage
                </button>
              )}
              {selectedDef.key === stage && (
                <span className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${selectedDef.bg} ${selectedDef.color} border border-current/20`}>
                  ● Current Stage
                </span>
              )}
              <button
                onClick={() => setSelectedStage(null)}
                className="p-1 hover:bg-black/5 rounded-lg transition-colors"
                title="Close"
              >
                <XMarkIcon className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
          <p className="text-[11px] text-gray-500 mb-3">
            Upload and manage documents for the <strong>{selectedDef.label}</strong> stage.
            Click any stage node above to switch stages.
          </p>

          {/* Documents for the selected stage */}
          <StageDocuments
            projectId={projectId}
            stage={selectedDef.key}
            stageColor={selectedDef.color}
            stageBg={selectedDef.bg}
          />
        </div>
      )}
    </div>
  );
}